// src/components/Layout/GenericLayout.tsx - Layout avec menu comptabilité

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { MenuProps } from 'antd';
import { Layout as AntLayout, Menu, Button, Avatar, Dropdown, Space, Typography, Drawer, Badge, Alert, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLayoutShell } from '../../contexts/LayoutShellContext';
import {
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  CalendarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  GiftOutlined,
  MessageOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  SwapOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  ShopOutlined,
  FileTextOutlined,
  TagOutlined,
  TruckOutlined,
  DeliveredProcedureOutlined,
  AuditOutlined,
  CheckSquareOutlined,
  CalculatorOutlined,
  BookOutlined,
  FileDoneOutlined,
  // Nouvelles icônes plus distinctives
  ClockCircleOutlined,
  InboxOutlined,
  BankOutlined,
  ToolOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  BellOutlined,
  DownOutlined,
  CreditCardOutlined,
  DollarOutlined,
  FileTextOutlined as ActivityReportOutlined,
  PartitionOutlined,
  CustomerServiceOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import NotificationCenter from '../Notifications/NotificationCenter';
import PushOptInModal from '../Notifications/PushOptInModal';
import DefaultSiteRequiredModal from '../DefaultSiteRequiredModal';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useChangeAppLocale } from '../../hooks/useChangeAppLocale';
import { buildLanguageMenuItems, parseLocaleMenuKey } from '../../utils/i18n/languageMenuItems';
import { usePermissions, useBusinessTerminology } from '../../hooks/usePermissions';
import { useRoleProfile } from '../../hooks/useRoleProfiles';
import { useNotificationSummary, useCheckPaymentPlanReminders } from '../../hooks/useNotifications';
import { useAppointmentRequestsCount } from '../../hooks/useAppointmentRequests';
import { usePickupRequestsPendingCount } from '../../hooks/usePickupRequests';
import { useNauvatekSupportUnreadCount, useMySupportUnreadCount } from '../../hooks/useSupportTickets';
import { useProducts, useTransactionFollowUpMessages } from '../../hooks/useFirestore';
import { getReceivablesUiLabels, isFeatureEnabled, BusinessFeatureKey } from '../../types/business';
import { useMyContractPaymentStatus } from '../../hooks/useBusinessContracts';
import { hasAnySettingsAccess } from '../../features/settings/hub/settingsNav';
import { canAccessSiteStats } from '../../utils/siteStatsAccess';

const { Header, Content } = AntLayout;

/** Mode test UX : tous les sous-menus visibles sont ouverts en permanence (aucune fermeture au clic). */
const MENU_ALL_SUBMENUS_ALWAYS_OPEN = false;

function setOpenKeysIfSubmenusChanged(prev: string[], next: string[]): string[] {
  if (prev.length === next.length) {
    const prevSig = [...prev].sort().join('|');
    const nextSig = [...next].sort().join('|');
    if (prevSig === nextSig) return prev;
  }
  return [...next];
}

function collectSubmenuKeysFromItems(items: MenuProps['items'] | undefined): string[] {
  const out: string[] = [];
  if (!items) return out;
  for (const raw of items) {
    if (!raw || typeof raw === 'string') continue;
    if ('type' in raw && raw.type === 'divider') continue;
    const item = raw as { key?: React.Key; children?: MenuProps['items'] };
    if (
      item.children &&
      Array.isArray(item.children) &&
      item.children.length > 0 &&
      item.key !== undefined &&
      item.key !== null
    ) {
      out.push(String(item.key));
      out.push(...collectSubmenuKeysFromItems(item.children));
    }
  }
  return Array.from(new Set(out));
}

interface GenericLayoutProps {
  children: React.ReactNode;
}

// Palette de couleurs avec le bleu demandé
const COLORS = {
  primary: '#E91E63',
  primaryLight: '#F48FB1',
  primaryDark: '#AD1457',
  secondary: '#00BCD4',
  accent: '#FFC107',
  blue: '#0A3780',
  dark: '#0A3780',
  light: '#F8F9FA',
  white: '#FFFFFF',
};

// Logo simple sans rond ni texte
const GenericLogo: React.FC<{ size?: number; collapsed?: boolean }> = ({ 
  size = 150, 
  collapsed = false 
}) => (
  <img 
    src="/LOGO.png"
    alt="Logo"
    style={{
      width: collapsed ? 48 : size,
      height: collapsed ? 48 : size,
      objectFit: 'contain',
      transition: 'all 0.3s ease',
    }}
    onError={(e) => {
      console.error('Logo non trouvé');
      e.currentTarget.style.display = 'none';
    }}
  />
);

const GenericLayout: React.FC<GenericLayoutProps> = ({ children }) => {
  // Iso mobile/desktop : menu = Drawer overlay (disparait totalement quand fermé)
  const [menuOpen, setMenuOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth > 768
  );
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [refreshingApp, setRefreshingApp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { registerLayoutShell } = useLayoutShell();
  
  // Référence pour préserver le scroll de la sidebar
  const menuScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);
  const { user, signOut, currentBusiness, userProfile, isSuperAdmin, isCSM, switchToSuperAdmin } = useAuth();
  const { t } = useAppTranslation();
  const { locale, changeLocale } = useChangeAppLocale();

  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    registerLayoutShell({
      isMenuOpen: menuOpen,
      isMobileShell: isMobile,
      openMenu,
      closeMenu,
    });
    return () => registerLayoutShell(null);
  }, [menuOpen, isMobile, openMenu, closeMenu, registerLayoutShell]);
  /** Super Admin : retour tableau de bord admin. CSM : pas d’espace admin — accès liste des établissements uniquement. */
  const showAdminSwitch = isSuperAdmin || isCSM;
  const permissions = usePermissions();
  const terminology = useBusinessTerminology();
  const { data: notificationSummary } = useNotificationSummary();
  const checkPaymentPlanReminders = useCheckPaymentPlanReminders();
  
  // Récupérer le profil de rôle du business actuel
  const roleProfileIdForCurrentBusiness = currentBusiness 
    ? userProfile?.roleProfileIds?.[currentBusiness.id]
    : undefined;
  const { data: currentRoleProfile } = useRoleProfile(roleProfileIdForCurrentBusiness);
  const appointmentRequestsCount = useAppointmentRequestsCount();
  const pickupCountEnabled =
    isFeatureEnabled(currentBusiness, 'pickup') &&
    (permissions?.canViewPickups ?? false);
  const { data: pickupPendingCount = 0 } = usePickupRequestsPendingCount({
    enabled: pickupCountEnabled,
  });
  const { data: supportUnreadCount = 0 } = useNauvatekSupportUnreadCount({
    enabled: showAdminSwitch,
  });
  const { data: helpUnreadCount = 0 } = useMySupportUnreadCount();
  
  // Hook pour récupérer les produits et calculer les alertes stock
  const { data: products = [] } = useProducts();
  const { data: messagesClientsItems = [] } = useTransactionFollowUpMessages();

  // Statut contrat/paiements — visible par tous les utilisateurs (espace client)
  const { paymentDelay, paymentDueSoon, pendingOtherPayments, pendingOtherPaymentLabels, hasContract, isLoading: contractStatusLoading } = useMyContractPaymentStatus(currentBusiness?.id);

  const pendingOtherFeesBannerMessage = useMemo(() => {
    if (pendingOtherPaymentLabels.length === 0) {
      return t('layout.otherFeesBannerGeneric');
    }
    if (pendingOtherPaymentLabels.length === 1) {
      return t('layout.otherFeesBannerOne', { labels: pendingOtherPaymentLabels[0] });
    }
    const last = pendingOtherPaymentLabels[pendingOtherPaymentLabels.length - 1];
    const labels = pendingOtherPaymentLabels.slice(0, -1).join(', ');
    return t('layout.otherFeesBannerMany', { labels, last });
  }, [pendingOtherPaymentLabels, t]);

  const hasFeature = React.useCallback(
    (feature: BusinessFeatureKey) => isFeatureEnabled(currentBusiness, feature),
    [currentBusiness],
  );

  useEffect(() => {
    if (!currentBusiness?.id || !isFeatureEnabled(currentBusiness, 'paymentPlans') || !permissions?.canViewPaymentPlans) {
      return;
    }
    checkPaymentPlanReminders.mutate();
    // Une fois par jour via localStorage dans le hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness?.id, permissions?.canViewPaymentPlans]);
  
  // Calcul des notifications d'alerte (basé uniquement sur les permissions)
  const stockAlerts = useMemo(() => {
    // Badge si l'utilisateur peut voir les produits (canViewProducts)
    if (!permissions?.canViewProducts || !products.length) return { lowStock: 0, outOfStock: 0 };
    
    const activeProducts = products.filter(p => p.isActive);
    const lowStock = activeProducts.filter(p => 
      p.currentStock <= p.minStockAlert && p.currentStock > 0
    ).length;
    const outOfStock = activeProducts.filter(p => p.currentStock === 0).length;
    
    return { lowStock, outOfStock };
  }, [products, permissions?.canViewProducts]);

  const messagesClientsBadgeCount = useMemo(() => {
    if (!hasFeature('messagesClients') || !permissions?.canViewMessagesClients) return 0;
    const now = Date.now();
    return messagesClientsItems.filter((item) => {
      if (item.status === 'sent' || item.status === 'cancelled') return false;
      if (item.status === 'skipped_no_phone' || item.status === 'skipped_opt_out' || item.status === 'suppressed_24h') return false;
      if (item.status === 'ready') return true;
      if (item.status === 'pending_delay') {
        return new Date(item.eligibleAt).getTime() <= now;
      }
      return false;
    }).length;
  }, [messagesClientsItems, permissions?.canViewMessagesClients, hasFeature]);

  // Restaurer les sous-menus ouverts depuis le dernier usage (persistance entre refresh)
  useEffect(() => {
    if (MENU_ALL_SUBMENUS_ALWAYS_OPEN) return;
    try {
      const stored =
        localStorage.getItem('app_open_menu_keys') ??
        localStorage.getItem('beauty_open_menu_keys'); // rétrocompat
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setOpenKeys(parsed);
        }
      }
    } catch (e) {
      console.warn('Impossible de restaurer les sous-menus ouverts', e);
    }
  }, []);

  // Sauvegarder les sous-menus ouverts à chaque changement
  useEffect(() => {
    if (MENU_ALL_SUBMENUS_ALWAYS_OPEN) return;
    try {
      localStorage.setItem('app_open_menu_keys', JSON.stringify(openKeys));
    } catch (e) {
      console.warn('Impossible de sauvegarder les sous-menus ouverts', e);
    }
  }, [openKeys]);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Shell app : bloquer le scroll du document (mobile Safari fait tout scroller sinon).
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      rootHeight: root?.style.height ?? '',
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    if (root) root.style.height = '100%';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      if (root) root.style.height = prev.rootHeight;
    };
  }, []);

  // Auto-ouvrir le bon sous-menu au chargement initial de la page seulement
  useEffect(() => {
    if (MENU_ALL_SUBMENUS_ALWAYS_OPEN) return;
    // Ne rien faire si un menu est déjà ouvert (évite le clignotement)
    if (openKeys.length > 0) {
      return;
    }
    
    // Déterminer quel sous-menu devrait être ouvert selon le pathname
    let submenuToOpen: string | null = null;
    
    if (location.pathname.startsWith('/pos/prestations-history') ||
        location.pathname.startsWith('/pos/sales-history') ||
        location.pathname.startsWith('/pos/reports')) {
      submenuToOpen = 'pos';
    } else if (
      location.pathname.startsWith('/inventory/') &&
      location.pathname !== '/inventory/categories' &&
      location.pathname !== '/inventory/packaging-units' &&
      location.pathname !== '/inventory/external-destinations'
    ) {
      submenuToOpen = 'inventory';
    } else if (location.pathname.startsWith('/suppliers') || location.pathname.startsWith('/expenses')) {
      submenuToOpen = 'purchases_expenses_menu';
    } else if (location.pathname.startsWith('/accounting/')) {
      submenuToOpen = 'accounting';
    } else if (location.pathname.startsWith('/employees') || location.pathname.startsWith('/payrolls')) {
      submenuToOpen = 'gestion';
    } else if (location.pathname.startsWith('/appointments')) {
      submenuToOpen = 'appointments_menu';
    } else if (
      location.pathname.startsWith('/activity/') &&
      location.pathname !== '/settings/pipeline'
    ) {
      submenuToOpen = 'activite';
    } else if (location.pathname.startsWith('/clients')) {
      submenuToOpen = 'clients_menu';
    }
    
    // Ouvrir le sous-menu approprié seulement au chargement initial
    if (submenuToOpen) {
      setOpenKeys([submenuToOpen]);
    }
  }, [location.pathname, openKeys]);

  // Sauvegarder la position du scroll avant chaque changement
  useEffect(() => {
    const menuElement = menuScrollRef.current;
    if (menuElement) {
      const handleScroll = () => {
        savedScrollPosition.current = menuElement.scrollTop;
      };
      
      menuElement.addEventListener('scroll', handleScroll);
      return () => menuElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Restaurer la position du scroll après ouverture/fermeture de sous-menu.
  // Délai pour s'exécuter après le scrollIntoView interne du Menu Ant Design.
  // Pendant un guide produit : ne pas écraser le scroll vers l’élément ciblé.
  useEffect(() => {
    const menuElement = menuScrollRef.current;
    if (!menuElement || savedScrollPosition.current <= 0) return;
    if (document.body.classList.contains('nauvatek-tour-active')) return;
    const pos = savedScrollPosition.current;
    const id = setTimeout(() => {
      if (document.body.classList.contains('nauvatek-tour-active')) return;
      if (menuScrollRef.current) {
        menuScrollRef.current.scrollTop = pos;
      }
    }, 80);
    return () => clearTimeout(id);
  }, [openKeys]);

  // Restaurer aussi le scroll après navigation (changement de route),
  // pour éviter un retour en haut lors du clic sur un item bas de menu.
  useEffect(() => {
    const menuElement = menuScrollRef.current;
    if (!menuElement || savedScrollPosition.current <= 0) return;
    if (document.body.classList.contains('nauvatek-tour-active')) return;
    const pos = savedScrollPosition.current;
    const id = setTimeout(() => {
      if (document.body.classList.contains('nauvatek-tour-active')) return;
      if (menuScrollRef.current) {
        menuScrollRef.current.scrollTop = pos;
      }
    }, 80);
    return () => clearTimeout(id);
  }, [location.pathname]);

  const menuItems = useMemo(() => {
    // ✅ IMPORTANT : Le menu est filtré par les features activées sur le business.
    // Même le Super Admin ne voit que les features activées (pas de bypass).
    // Caisse (POS) : Nouvelle Transaction = entrée top-level ; le sous-menu regroupe historiques / OF / rapports
    const canSellPrestations =
      hasFeature('pos') && hasFeature('services') && !!permissions?.canViewPrestations;
    const canSellProducts =
      hasFeature('pos') && !!permissions?.canViewProductSales;
    const canCreateTransaction = canSellPrestations || canSellProducts;
    const posChildren = hasFeature('pos') ? [
      ...(canSellPrestations
        ? [{
            key: '/pos/prestations-history',
            icon: <ClockCircleOutlined />,
            label: <span data-tour="nav-pos-prestations-history">{t('menu.prestationsHistory')}</span>,
            tourLeaf: 'nav-pos-prestations-history',
          }]
        : []),
      ...(canSellProducts
        ? [{
            key: '/pos/sales-history',
            icon: <ShoppingCartOutlined />,
            label: <span data-tour="nav-pos-sales-history">{t('menu.productsHistory')}</span>,
            tourLeaf: 'nav-pos-sales-history',
          }]
        : []),
      ...((canSellPrestations || canSellProducts || permissions?.canViewCashReports)
        ? [{
            key: '/pos/reports',
            icon: <BarChartOutlined />,
            label: <span data-tour="nav-pos-cash-reports">{t('menu.reports')}</span>,
            tourLeaf: 'nav-pos-cash-reports',
          }]
        : []),
    ].filter(Boolean) : [];
    const hasPosHistory = posChildren.length > 0;

    // RDV : sous-items visibles (Calendrier, Demandes RDV)
    const rdvBadge = appointmentRequestsCount > 0 ? (
      <span style={{ marginLeft: '8px', backgroundColor: '#1890ff', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>
        {appointmentRequestsCount}
      </span>
    ) : null;
    const rdvChildren = hasFeature('appointments') ? [
      ...(permissions?.canViewAppointments
        ? [{
            key: '/appointments',
            icon: <CalendarOutlined />,
            label: <span data-tour="nav-appointments-calendar">{t('menu.calendar')}</span>,
          }]
        : []),
      ...(permissions?.canViewAppointmentRequests
        ? [{
            key: '/appointment-requests',
            icon: <BellOutlined />,
            label: (
              <span data-tour="nav-appointments-requests">
                {t('menu.appointmentRequests')} {rdvBadge}
              </span>
            ),
          }]
        : []),
    ].filter(Boolean) : [];
    const hasRdv = rdvChildren.length > 0;
    const messagesClientsBadge = messagesClientsBadgeCount > 0 ? (
      <span style={{ marginLeft: '8px', backgroundColor: '#1890ff', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>
        {messagesClientsBadgeCount}
      </span>
    ) : null;

    return [
    // 1. Tableau de bord en premier
    ...(hasFeature('dashboard') && permissions?.canViewDashboard ? [{
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <span data-tour="nav-dashboard">{t('menu.dashboard')}</span>,
    }] : []),

    // 2. Nouvelle Transaction (hors sous-menu)
    ...(canCreateTransaction ? [{
      key: '/pos/transaction',
      icon: <ShopOutlined />,
      label: <span data-tour="nav-pos">{t('menu.newTransaction')}</span>,
    }] : []),

    // 3. Paiement en plusieurs (sous Transaction)
    ...(hasFeature('paymentPlans') && permissions?.canViewPaymentPlans ? [{
      key: '/payment-plans',
      icon: <DollarOutlined />,
      label: (
        <span data-tour="nav-payment-plans">
          {getReceivablesUiLabels(currentBusiness).menuLabel}
        </span>
      ),
    }] : []),

    // 3b. Fabrication (sous paiement multi)
    ...(hasFeature('manufacturing') && permissions?.canViewManufacturingOrders
      ? [{
          key: '/manufacturing/orders',
          icon: <ToolOutlined />,
          label: <span data-tour="nav-manufacturing">{t('menu.manufacturingOrders')}</span>,
        }]
      : []),

    // 4. Clients (sous paiement multi)
    ...(hasFeature('clients') && permissions?.canViewClients ? [{
      key: '/clients',
      icon: <UserOutlined />,
      label: (
        <span data-tour="nav-clients">
          {terminology.clients}
          {(notificationSummary?.byType.birthday || 0) > 0 && (
            <span style={{ 
              marginLeft: '8px',
              backgroundColor: '#52c41a',
              color: 'white',
              borderRadius: '10px',
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: 'bold'
            }}>
              {notificationSummary?.byType.birthday || 0}
            </span>
          )}
        </span>
      ),
    }] : []),

    // Ramassages
    ...(hasFeature('pickup') && permissions?.canViewPickups ? [{
      key: '/pickups',
      icon: <DeliveredProcedureOutlined />,
      label: (
        <span data-tour="nav-pickups">
          {t('menu.pickups')}
          {pickupPendingCount > 0 && (
            <span style={{
              marginLeft: '8px',
              backgroundColor: '#13c2c2',
              color: 'white',
              borderRadius: '10px',
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: 'bold',
            }}>
              {pickupPendingCount}
            </span>
          )}
        </span>
      ),
    }] : []),

    // 5. Historiques (OF, historiques, rapports)
    ...(hasPosHistory ? (posChildren.length === 1
      ? [{
          key: posChildren[0].key,
          icon: posChildren[0].icon,
          label: (
            <span data-tour="nav-pos-histories">
              {posChildren[0].label}
            </span>
          ),
        }]
      : [{
          key: 'pos',
          icon: <ClockCircleOutlined />,
          label: <span data-tour="nav-pos-histories">{t('menu.histories')}</span>,
          children: posChildren,
        }]
    ) : []),

    ...(hasFeature('marketing') && permissions?.canViewMarketing ? [{
      key: '/marketing',
      icon: <GiftOutlined />,
      label: <span data-tour="nav-marketing">{t('menu.marketing')}</span>,
    }] : []),
    ...(hasFeature('messagesClients') && permissions?.canViewMessagesClients ? [{
      key: '/messages-clients',
      icon: <MessageOutlined />,
      label: (
        <span data-tour="nav-messages-clients">
          {t('menu.messagesClients')} {messagesClientsBadge}
        </span>
      ),
    }] : []),
    // RDV
    ...(hasRdv ? (rdvChildren.length === 1
      ? [{
          key: rdvChildren[0].key,
          icon: rdvChildren[0].icon,
          label: <span data-tour="nav-appointments">{rdvChildren[0].label}</span>,
        }]
      : [{
          key: 'appointments_menu',
          icon: <CalendarOutlined />,
          label: <span data-tour="nav-appointments">{terminology.appointments} {rdvBadge}</span>,
          children: rdvChildren,
        }]
    ) : []),
    
    ...(hasFeature('referents') && permissions?.canViewReferents ? [{
      key: '/referents',
      icon: <TeamOutlined />,
      label: <span data-tour="nav-referents">{t('menu.referents')}</span>,
    }] : []),

    // Menu Stock : 1 sous-item → lien direct ; 2+ → menu
    ...(function () {
      const invChildren = hasFeature('inventory') ? [
        ...(permissions?.canViewProducts
          ? [{
              key: '/inventory/products',
              icon: <AppstoreOutlined />,
              label: <span data-tour="nav-inventory-products">{t('menu.products')}</span>,
            }]
          : []),
        ...(permissions?.canViewStockMovements
          ? [{
              key: '/inventory/movements',
              icon: <SwapOutlined />,
              label: <span data-tour="nav-inventory-movements">{t('menu.movements')}</span>,
            }]
          : []),
        ...(hasFeature('inventoryLosses') && permissions?.canViewInventoryLosses
          ? [{
              key: '/inventory/losses',
              icon: <WarningOutlined />,
              label: <span data-tour="nav-inventory-losses">{t('menu.losses')}</span>,
            }]
          : []),
        ...(hasFeature('inventoryExternalDispatch') && permissions?.canViewExternalDispatches
          ? [{
              key: '/inventory/external-dispatches',
              icon: <DeliveredProcedureOutlined />,
              label: <span data-tour="nav-inventory-external">{t('menu.externalDispatches')}</span>,
            }]
          : []),
        ...(permissions?.canViewInventoryAudit
          ? [{
              key: '/inventory/audit',
              icon: <AuditOutlined />,
              label: <span data-tour="nav-inventory-audit">{t('menu.physicalInventories')}</span>,
            }]
          : []),
        ...(permissions?.canViewInventoryReports
          ? [{
              key: '/inventory/dashboard',
              icon: <BarChartOutlined />,
              label: <span data-tour="nav-inventory-reports">{t('menu.stockReports')}</span>,
            }]
          : []),
      ].filter(Boolean) : [];
      if (invChildren.length === 0) return [];
      const stockLabelInner = (stockAlerts.lowStock > 0 || stockAlerts.outOfStock > 0) ? (
        <span>{t('menu.stock')} <span style={{ marginLeft: '8px', backgroundColor: stockAlerts.outOfStock > 0 ? '#ff4d4f' : '#faad14', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>{stockAlerts.outOfStock > 0 ? '!' : stockAlerts.lowStock}</span></span>
      ) : t('menu.stock');
      const stockLabel = <span data-tour="nav-inventory">{stockLabelInner}</span>;
      return invChildren.length === 1
        ? [{
            key: invChildren[0].key,
            icon: invChildren[0].icon,
            label: <span data-tour="nav-inventory">{invChildren[0].label}</span>,
          }]
        : [{ key: 'inventory', icon: <InboxOutlined />, label: stockLabel, children: invChildren }];
    })(),
    
    // Menu Achats & Dépenses : 1 sous-item → lien direct ; 2+ → menu
    ...(function () {
      const purchChildren = hasFeature('purchasesExpenses') ? [
        ...(permissions?.canViewSuppliers
          ? [{
              key: '/suppliers',
              icon: <TruckOutlined />,
              label: <span data-tour="nav-suppliers">{t('menu.suppliers')}</span>,
            }]
          : []),
        ...(permissions?.canViewDeliveries
          ? [{
              key: '/suppliers/deliveries',
              icon: <DeliveredProcedureOutlined />,
              label: <span data-tour="nav-deliveries">{terminology.deliveries}</span>,
            }]
          : []),
        ...(permissions?.canViewExpenses
          ? [{
              key: '/expenses',
              icon: <ShoppingOutlined />,
              label: <span data-tour="nav-expenses">{t('menu.expenses')}</span>,
            }]
          : []),
        ...(hasFeature('expenseNotes') && permissions?.canViewExpenseNotes
          ? [{
              key: '/expense-notes',
              icon: <ShoppingOutlined />,
              label: <span data-tour="nav-expense-notes">{t('menu.expenseNotes')}</span>,
            }]
          : []),
      ].filter(Boolean) : [];
      if (purchChildren.length === 0) return [];
      return purchChildren.length === 1
        ? [{
            key: purchChildren[0].key,
            icon: purchChildren[0].icon,
            label: <span data-tour="nav-purchases">{purchChildren[0].label}</span>,
          }]
        : [{
            key: 'purchases_expenses_menu',
            icon: <TruckOutlined />,
            label: <span data-tour="nav-purchases">{t('menu.purchasesExpenses')}</span>,
            children: purchChildren,
          }];
    })(),
    
    // ✅ NOUVEAU : Menu Comptabilité
    ...(hasFeature('accounting') && permissions?.canViewAccounting ? [{
      key: 'accounting',
      icon: <BankOutlined />,
      label: <span data-tour="nav-accounting">{t('menu.accounting')}</span>,
      children: [
        {
          key: '/accounting/dashboard',
          icon: <BarChartOutlined />,
          label: <span data-tour="nav-accounting-dashboard">{t('menu.accountingDashboard')}</span>,
        },
        {
          key: '/accounting/journals',
          icon: <BookOutlined />,
          label: <span data-tour="nav-accounting-journals">{t('menu.accountingJournals')}</span>,
        },
        {
          key: '/accounting/tax-returns',
          icon: <FileDoneOutlined />,
          label: <span data-tour="nav-accounting-tax">{t('menu.taxReturns')}</span>,
        },
      ],
    }] : []),
    
    // ✅ Menu Prestations regroupé
    ...(hasFeature('services') && permissions?.canViewServices ? [{
      key: '/services',
      icon: <ToolOutlined />,
      label: <span data-tour="nav-services">{t('menu.services')}</span>,
    }] : []),

    // ✅ Menu Employés / paies / indicateur : 1 sous-item → lien direct ; 2+ → menu
    // performanceIndicators est autonome (contrat) : visible même sans employeesPayroll.
    ...(function () {
      const gestionChildren = [
        ...(hasFeature('employeesPayroll') && permissions?.canViewEmployees
          ? [{
              key: '/employees',
              icon: <TeamOutlined />,
              label: <span data-tour="nav-employees">{t('menu.employees')}</span>,
            }]
          : []),
        ...(hasFeature('employeesPayroll') && permissions?.canViewPayrolls
          ? [{
              key: '/payrolls',
              icon: <DollarOutlined />,
              label: <span data-tour="nav-payrolls">{t('menu.payrolls')}</span>,
            }]
          : []),
        ...(hasFeature('performanceIndicators') && permissions?.canViewPerformanceObjectives
          ? [{
              key: '/performance-indicators',
              icon: <BarChartOutlined />,
              label: <span data-tour="nav-performance">{t('menu.performanceIndicators')}</span>,
            }]
          : []),
      ].filter(Boolean);
      if (gestionChildren.length === 0) return [];
      const menuLabel =
        hasFeature('employeesPayroll')
          ? t('menu.employeesPayroll')
          : t('menu.performanceIndicators');
      return gestionChildren.length === 1
        ? [{
            key: gestionChildren[0].key,
            icon: gestionChildren[0].icon,
            label: <span data-tour="nav-hr">{gestionChildren[0].label}</span>,
          }]
        : [{
            key: 'gestion',
            icon: <TeamOutlined />,
            label: <span data-tour="nav-hr">{menuLabel}</span>,
            children: gestionChildren,
          }];
    })(),

    // Menu Activité : rapports et opportunités = modules distincts (features séparées)
    ...(function () {
      const activiteChildren = [
        ...(hasFeature('activityReports') && permissions?.canViewActivityReports
          ? [{
              key: '/activity/reports',
              icon: <ActivityReportOutlined />,
              label: <span data-tour="nav-activity-reports">{t('menu.activityReports')}</span>,
            }]
          : []),
        ...(hasFeature('opportunities') && permissions?.canViewSalesPipeline
          ? [{
              key: '/activity/opportunities',
              icon: <PartitionOutlined />,
              label: <span data-tour="nav-opportunities">{t('menu.opportunities')}</span>,
            }]
          : []),
      ];
      if (activiteChildren.length === 0) return [];
      return activiteChildren.length === 1
        ? [{
            key: activiteChildren[0].key,
            icon: activiteChildren[0].icon,
            label: <span data-tour="nav-activity">{activiteChildren[0].label}</span>,
          }]
        : [{
            key: 'activite',
            icon: <ActivityReportOutlined />,
            label: <span data-tour="nav-activity">{t('menu.activity')}</span>,
            children: activiteChildren,
          }];
    })(),
  ]; }, [permissions, terminology, stockAlerts, appointmentRequestsCount, pickupPendingCount, currentBusiness, messagesClientsBadgeCount, hasFeature, t]);

  const allSubmenuKeysForMenu = useMemo(
    () => collectSubmenuKeysFromItems(menuItems),
    [menuItems]
  );

  useEffect(() => {
    if (!MENU_ALL_SUBMENUS_ALWAYS_OPEN) return;
    const next = allSubmenuKeysForMenu;
    setOpenKeys((prev) => setOpenKeysIfSubmenusChanged(prev, next));
  }, [allSubmenuKeysForMenu]);

  const handleMenuClick = ({ key }: { key: string }) => {
    if (menuScrollRef.current) {
      savedScrollPosition.current = menuScrollRef.current.scrollTop;
    }
    if (key === 'admin_switch') {
      switchToSuperAdmin();
      if (isMobile) closeMenu();
      return;
    }
    // Fermer le drawer mobile uniquement sur navigation réelle (pas ouverture de sous-menu)
    const nonNavigableKeys = [
      'inventory',
      'pos',
      'purchases_expenses_menu',
      'accounting',
      'gestion',
      'appointments_menu',
      'activite',
      'clients_menu',
      'services',
    ];
    
    if (!nonNavigableKeys.includes(key)) {
      navigate(key);
      
      // Déterminer quel sous-menu contient cet item
      const submenuMapping: { [key: string]: string } = {
        '/pos/prestations-history': 'pos',
        '/pos/sales-history': 'pos',
        '/pos/reports': 'pos',
        '/inventory/dashboard': 'inventory',
        '/inventory/products': 'inventory',
        '/inventory/audit': 'inventory',
        '/inventory/movements': 'inventory',
        '/inventory/losses': 'inventory',
        '/inventory/external-dispatches': 'inventory',
        '/suppliers': 'purchases_expenses_menu',
        '/suppliers/deliveries': 'purchases_expenses_menu',
        '/expenses': 'purchases_expenses_menu',
        '/expense-notes': 'purchases_expenses_menu',
        '/accounting/dashboard': 'accounting',
        '/accounting/journals': 'accounting',
        '/accounting/tax-returns': 'accounting',
        '/employees': 'gestion',
        '/payrolls': 'gestion',
        '/performance-indicators': 'gestion',
        '/appointments': 'appointments_menu',
        '/appointment-requests': 'appointments_menu',
        '/activity/reports': 'activite',
        '/activity/opportunities': 'activite',
        '/services': 'services',
        '/clients': 'clients_menu',
      };
      
      if (!MENU_ALL_SUBMENUS_ALWAYS_OPEN) {
        const parentSubmenu = submenuMapping[key];
        setOpenKeys(parentSubmenu ? [parentSubmenu] : []);
      }

      if (isMobile) closeMenu();
    }
  };

  const handleOpenChange = (keys: string[]) => {
    if (menuScrollRef.current) {
      savedScrollPosition.current = menuScrollRef.current.scrollTop;
    }
    if (MENU_ALL_SUBMENUS_ALWAYS_OPEN) {
      const next = allSubmenuKeysForMenu;
      setOpenKeys((prev) => setOpenKeysIfSubmenusChanged(prev, next));
      return;
    }
    const submenuKeys = [
      'pos',
      'inventory',
      'purchases_expenses_menu',
      'accounting',
      'gestion',
      'services',
      'appointments_menu',
      'activite',
      'clients_menu',
    ];

    // Accordion : un seul sous-menu ouvert à la fois (Transactions inclus)
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (latestOpenKey && submenuKeys.includes(latestOpenKey)) {
      setOpenKeys([latestOpenKey]);
      return;
    }

    setOpenKeys(keys.filter((key) => submenuKeys.includes(key)));
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      type: 'group',
      label: t('account.group'),
      children: [
        ...(showAdminSwitch
          ? [
              {
                key: 'admin_switch',
                icon: <SettingOutlined />,
                label: isCSM ? t('account.switchBusiness') : t('account.superAdmin'),
              },
            ]
          : []),
        {
          key: 'profile',
          icon: <UserOutlined />,
          label: t('account.profile'),
        },
        {
          key: 'documentation',
          icon: <BookOutlined />,
          label: t('account.documentation'),
        },
        ...(canAccessSiteStats({ permissions, business: currentBusiness })
          ? [
              {
                key: 'site_stats',
                icon: <ShopOutlined />,
                label: t('account.siteStats'),
              },
            ]
          : []),
        ...(hasAnySettingsAccess({ permissions, business: currentBusiness })
          ? [
              {
                key: 'settings',
                icon: <SettingOutlined />,
                label: (
                  <span data-tour="account-settings">{t('account.settings')}</span>
                ),
              },
            ]
          : []),
        ...(hasFeature('licencePayment') && permissions?.canViewLicencePayment
          ? [
              {
                key: 'payment',
                icon: <CreditCardOutlined />,
                label: t('account.payment'),
              },
            ]
          : []),
      ],
    },
    ...buildLanguageMenuItems({ locale, t }),
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('actions.logout'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    const nextLocale = parseLocaleMenuKey(key);
    if (nextLocale) {
      void changeLocale(nextLocale);
      return;
    }
    if (key === 'admin_switch') {
      if (isCSM) {
        navigate('/business-selection');
      } else {
        switchToSuperAdmin();
      }
      return;
    }
    if (key === 'profile') {
      navigate('/mon-compte');
      return;
    }
    if (key === 'documentation') {
      window.open('/documentation', '_blank');
      return;
    }
    if (key === 'site_stats') {
      navigate('/site-stats');
      return;
    }
    if (key === 'settings') {
      navigate('/settings');
      return;
    }
    if (key === 'payment') {
      navigate('/mon-paiement');
    }
  };

  const handleRefreshApp = () => {
    if (refreshingApp) return;
    setRefreshingApp(true);
    void queryClient
      .invalidateQueries()
      .then(() => {
        message.success(t('account.refreshAppDone'));
      })
      .finally(() => {
        setRefreshingApp(false);
      });
  };

  const toggleSidebar = () => {
    setMenuOpen((open) => !open);
  };

  const getSelectedKeys = () => {
    if (location.pathname.startsWith('/inventory/')) {
      return [location.pathname];
    }
    if (location.pathname.startsWith('/pos/')) {
      return [location.pathname];
    }
    if (location.pathname.startsWith('/suppliers/')) {
      return [location.pathname];
    }
    if (location.pathname.startsWith('/accounting/')) {
      return [location.pathname];
    }
    if (location.pathname.startsWith('/services/')) {
      return [location.pathname];
    }
    if (location.pathname.startsWith('/employees')) {
      return [location.pathname];
    }
    if (location.pathname === '/appointments' || location.pathname === '/appointment-requests') {
      return [location.pathname];
    }
    return [location.pathname];
  };

  // Styles CSS personnalisés pour le menu avec fond bleu foncé
  const menuStyles = `
    .ant-menu {
      background: ${COLORS.dark} !important;
      padding-bottom: 20px !important;
    }
    .ant-menu-item {
      color: rgba(255, 255, 255, 0.85) !important;
      border-radius: 8px !important;
      margin: 4px 8px !important;
    }
    .ant-menu-item .ant-menu-item-icon {
      color: rgba(255, 255, 255, 0.85) !important;
    }
    .ant-menu-item-selected {
      background-color: rgba(255, 255, 255, 0.15) !important;
      border-right: none !important;
    }
    .ant-menu-item-selected .ant-menu-item-icon,
    .ant-menu-item-selected .ant-menu-title-content {
      color: #ffffff !important;
      font-weight: 600 !important;
    }
    .ant-menu-item:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
    }
    .ant-menu-item:hover .ant-menu-item-icon,
    .ant-menu-item:hover .ant-menu-title-content {
      color: #ffffff !important;
    }
    .ant-menu-submenu > .ant-menu-submenu-title {
      color: rgba(255, 255, 255, 0.85) !important;
      border-radius: 8px !important;
      margin: 4px 8px !important;
    }
    .ant-menu-submenu-title .ant-menu-item-icon {
      color: rgba(255, 255, 255, 0.85) !important;
    }
    .ant-menu-submenu-title:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
    }
    .ant-menu-submenu-title:hover .ant-menu-item-icon,
    .ant-menu-submenu-title:hover .ant-menu-title-content {
      color: #ffffff !important;
    }
    /* Styles pour les groupes ouverts - PAS de style "sélectionné" */
    .ant-menu-submenu-open > .ant-menu-submenu-title {
      background-color: transparent !important;
      color: rgba(255, 255, 255, 0.85) !important;
    }
    .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-item-icon,
    .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-title-content {
      color: rgba(255, 255, 255, 0.85) !important;
      font-weight: normal !important;
    }
    .ant-menu-submenu-arrow {
      color: rgba(255, 255, 255, 0.65) !important;
    }
    .ant-menu-sub {
      background: rgba(0, 0, 0, 0.1) !important;
    }
    .ant-menu-sub .ant-menu-item {
      color: rgba(255, 255, 255, 0.75) !important;
    }
    .ant-menu-sub .ant-menu-item:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      color: #ffffff !important;
    }
    .ant-menu-sub .ant-menu-item-selected {
      background-color: rgba(255, 255, 255, 0.15) !important;
      color: #ffffff !important;
      font-weight: 600 !important;
    }
  `;

  const sidebarContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: COLORS.dark,
      }}
    >
      <style>{menuStyles}</style>
      <div
        ref={menuScrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          background: COLORS.dark,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={getSelectedKeys()}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            borderRight: 0,
            marginTop: 8,
            background: COLORS.dark,
          }}
          theme="dark"
        />
      </div>
    </div>
  );

  return (
    <AntLayout
      style={{
        height: '100dvh',
        maxHeight: '100dvh',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        overflowX: 'hidden',
      }}
    >
      {/* Menu latéral — Drawer : mask+clic dehors sur mobile ; panneau fixe sans mask/croix sur desktop */}
      <Drawer
        title={
          <div style={{ textAlign: 'center' }}>
            <GenericLogo size={120} />
          </div>
        }
        placement="left"
        width={260}
        forceRender
        onClose={closeMenu}
        open={menuOpen}
        closable={false}
        mask={isMobile}
        maskClosable={isMobile}
        keyboard={isMobile}
        zIndex={1000}
        styles={{
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            background: COLORS.dark,
          },
          header: {
            borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
            background: COLORS.dark,
            color: COLORS.white,
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      <AntLayout
        style={{
          marginLeft: !isMobile && menuOpen ? 260 : 0,
          width: !isMobile && menuOpen ? 'calc(100% - 260px)' : '100%',
          maxWidth: !isMobile && menuOpen ? 'calc(100% - 260px)' : '100%',
          transition: 'margin-left 0.2s, width 0.2s, max-width 0.2s',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* Navbar + bandeau : hors zone de scroll (fixe sur mobile et desktop) */}
        <div
          style={{
            flexShrink: 0,
            zIndex: 1000,
            background: COLORS.white,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            /* Fallback si padding body insuffisant en PWA iOS */
            paddingTop: isMobile ? 'max(0px, env(safe-area-inset-top, 0px))' : 0,
          }}
        >
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: COLORS.white,
            borderBottom: `1px solid rgba(233, 30, 99, 0.1)`,
            boxShadow: `0 2px 8px rgba(0, 0, 0, 0.05)`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: isMobile ? 6 : 8,
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            height: 64,
            lineHeight: '64px',
            position: 'relative',
          }}
        >
          {/* Gauche : burger uniquement */}
          <Button
            type="text"
            data-tour="menu-toggle"
            aria-label={t('layout.openMenuAria')}
            icon={menuOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={toggleSidebar}
            style={{
              fontSize: '16px',
              width: isMobile ? 44 : 64,
              height: 64,
              color: COLORS.dark,
              flexShrink: 0,
            }}
          />

          {/* Droite : actions (cercles en mobile) + notifs + compte */}
          <Space size={isMobile ? 6 : 'middle'} style={{ flexShrink: 0, alignItems: 'center' }}>
            {showAdminSwitch && (
              <Badge count={supportUnreadCount} size="small" offset={isMobile ? [-2, 2] : undefined}>
                <Button
                  type={isMobile ? 'primary' : 'default'}
                  shape={isMobile ? 'circle' : 'default'}
                  icon={<CustomerServiceOutlined />}
                  onClick={() => navigate('/super-admin/support-tickets')}
                  style={
                    isMobile
                      ? {
                          width: 36,
                          height: 36,
                          minWidth: 36,
                          padding: 0,
                          background: '#E91E63',
                          borderColor: '#E91E63',
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }
                      : {
                          color: '#E91E63',
                          borderColor: '#E91E63',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: 600,
                          flexShrink: 0,
                        }
                  }
                  title="Support clients"
                >
                  {!isMobile && 'Support'}
                </Button>
              </Badge>
            )}
            {showAdminSwitch && (
              <Button
                type={isMobile ? 'primary' : 'default'}
                shape={isMobile ? 'circle' : 'default'}
                icon={<SettingOutlined />}
                onClick={() =>
                  isCSM ? navigate('/business-selection') : switchToSuperAdmin()
                }
                style={
                  isMobile
                    ? {
                        width: 36,
                        height: 36,
                        minWidth: 36,
                        padding: 0,
                        background: '#722ed1',
                        borderColor: '#722ed1',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : {
                        color: '#722ed1',
                        borderColor: '#722ed1',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 600,
                        flexShrink: 0,
                      }
                }
                title={
                  isCSM ? t('layout.csmBusinessSwitchTitle') : t('layout.superAdminBack')
                }
              >
                {!isMobile && (isCSM ? t('layout.establishments') : t('layout.superAdmin'))}
              </Button>
            )}

            {userProfile && currentBusiness && (() => {
              const canSwitchBusiness =
                isSuperAdmin === true || (userProfile.businessIds?.length ?? 0) > 1;
              const title = canSwitchBusiness
                ? t('layout.businessSwitch')
                : currentBusiness.name;
              return (
                <Button
                  type={isMobile ? 'primary' : 'text'}
                  shape={isMobile ? 'circle' : undefined}
                  icon={<SwapOutlined />}
                  onClick={() => {
                    if (canSwitchBusiness) {
                      navigate('/business-selection');
                    }
                  }}
                  disabled={!canSwitchBusiness}
                  style={
                    isMobile
                      ? {
                          width: 36,
                          height: 36,
                          minWidth: 36,
                          padding: 0,
                          background: canSwitchBusiness ? '#1890ff' : '#bfbfbf',
                          borderColor: canSwitchBusiness ? '#1890ff' : '#bfbfbf',
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: canSwitchBusiness ? 1 : 0.65,
                        }
                      : {
                          color: COLORS.dark,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: canSwitchBusiness ? 'pointer' : 'default',
                        }
                  }
                  title={title}
                >
                  {!isMobile && currentBusiness.name}
                </Button>
              );
            })()}

            <NotificationCenter />

            <Button
              type={isMobile ? 'primary' : 'default'}
              shape={isMobile ? 'circle' : 'default'}
              icon={<SyncOutlined spin={refreshingApp} />}
              onClick={handleRefreshApp}
              loading={!isMobile && refreshingApp}
              disabled={refreshingApp}
              title={t('account.refreshApp')}
              aria-label={t('account.refreshApp')}
              data-tour="refresh-app"
              style={
                isMobile
                  ? {
                      width: 36,
                      height: 36,
                      minWidth: 36,
                      padding: 0,
                      background: '#1890ff',
                      borderColor: '#1890ff',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  : {
                      color: '#1890ff',
                      borderColor: '#1890ff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: 600,
                      flexShrink: 0,
                    }
              }
            >
              {!isMobile && t('account.refreshApp')}
            </Button>

            <Badge count={helpUnreadCount} size="small" offset={isMobile ? [-2, 2] : undefined}>
              <Button
                type={isMobile ? 'primary' : 'default'}
                shape={isMobile ? 'circle' : 'default'}
                icon={<QuestionCircleOutlined />}
                onClick={() => navigate('/aide')}
                data-tour="help-nav"
                style={
                  isMobile
                    ? {
                        width: 36,
                        height: 36,
                        minWidth: 36,
                        padding: 0,
                        background: '#d946ef',
                        borderColor: '#d946ef',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : {
                        color: '#d946ef',
                        borderColor: '#d946ef',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 600,
                        flexShrink: 0,
                      }
                }
                title={t('layout.helpTitle')}
              >
                {!isMobile && t('layout.helpShort')}
              </Button>
            </Badge>
            
            <Dropdown
              data-testid="user-menu"
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
              arrow
            >
              <button
                type="button"
                aria-label={t('layout.accountMenuAria')}
                title={t('layout.accountMenuTitle')}
                data-tour="account-menu"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  margin: 0,
                  padding: isMobile ? '4px 8px' : '4px 10px 4px 4px',
                  border: `1px solid ${COLORS.dark}22`,
                  borderRadius: 999,
                  background: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.borderColor = `${COLORS.dark}55`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = `${COLORS.dark}22`;
                }}
              >
                <Avatar
                  size={32}
                  icon={<UserOutlined />}
                  style={{
                    backgroundColor: COLORS.dark,
                    color: COLORS.white,
                    flexShrink: 0,
                  }}
                />
                <div
                  style={{
                    display: isMobile ? 'none' : 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    lineHeight: 1.25,
                    minWidth: 0,
                    textAlign: 'left',
                  }}
                >
                  <span
                    style={{
                      color: '#262626',
                      fontWeight: 600,
                      fontSize: 13,
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {userProfile?.name || user?.displayName || t('account.profile')}
                  </span>
                  <span
                    style={{
                      color: '#8c8c8c',
                      fontSize: 11,
                    }}
                  >
                    {currentRoleProfile?.name || t('layout.roleMenuFallback')}
                  </span>
                </div>
                <DownOutlined
                  style={{
                    fontSize: 10,
                    color: '#8c8c8c',
                    marginLeft: isMobile ? 0 : 2,
                  }}
                />
              </button>
            </Dropdown>
          </Space>
        </Header>

        {/* Bandeau contrat — messages selon retard, paiement proche ou frais en attente (visible par tous) */}
        {currentBusiness && hasContract && !contractStatusLoading && (paymentDelay || paymentDueSoon || pendingOtherPayments) && (
          <div style={{ padding: '0 24px', background: paymentDelay ? '#fff2f0' : '#fffbe6', borderBottom: `1px solid ${paymentDelay ? '#ffccc7' : '#ffe58f'}` }}>
            {paymentDelay ? (
              <Alert
                message={t('layout.licenceOverdueBanner')}
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ margin: 0, border: 'none', background: 'transparent', padding: '8px 0' }}
                action={
                  hasFeature('licencePayment') && permissions?.canViewLicencePayment ? (
                    <Button size="small" type="primary" danger onClick={() => navigate('/mon-paiement')}>
                      {t('layout.payButton')}
                    </Button>
                  ) : undefined
                }
              />
            ) : paymentDueSoon ? (
              <Alert
                message={t('layout.licenceDueSoonBanner')}
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ margin: 0, border: 'none', background: 'transparent', padding: '8px 0' }}
                action={
                  hasFeature('licencePayment') && permissions?.canViewLicencePayment ? (
                    <Button size="small" type="primary" onClick={() => navigate('/mon-paiement')}>
                      {t('layout.payButton')}
                    </Button>
                  ) : undefined
                }
              />
            ) : pendingOtherPayments ? (
              <Alert
                message={pendingOtherFeesBannerMessage}
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ margin: 0, border: 'none', background: 'transparent', padding: '8px 0' }}
                action={
                  hasFeature('licencePayment') && permissions?.canViewLicencePayment ? (
                    <Button size="small" type="primary" onClick={() => navigate('/mon-paiement')}>
                      {t('layout.payButton')}
                    </Button>
                  ) : undefined
                }
              />
            ) : null}
          </div>
        )}
        </div>

        <Content
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            maxWidth: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            margin: isMobile ? '8px' : '12px',
            padding: isMobile ? '16px 16px' : '24px 24px',
            paddingBottom: isMobile
              ? 'max(16px, env(safe-area-inset-bottom, 0px))'
              : '24px',
            background: COLORS.white,
            borderRadius: '12px',
            boxShadow: `0 2px 8px rgba(0, 0, 0, 0.05)`,
            border: `1px solid rgba(233, 30, 99, 0.1)`,
          }}
        >
          {children}
        </Content>
      </AntLayout>
      <DefaultSiteRequiredModal />
      <PushOptInModal />
    </AntLayout>
  );
};

export default GenericLayout;